import { useState, useRef, useEffect, useCallback } from 'react';
import {
    fetchWhatsappConversations,
    fetchWhatsappMessages,
    sendWhatsappReply,
} from '../util/whatsappChatApi';
import { getWhatsAppStreamUrl } from '../api/centralWhatsappApi';
import './Chat.css';
const STATUS_CONFIG = {
    open: { label: 'Open', color: '#16A34A', bg: '#DCFCE7' },
    resolved: { label: 'Resolved', color: '#64748B', bg: '#F1F5F9' },
    closed: { label: 'Closed', color: '#DC2626', bg: '#FEE2E2' },
    pending: { label: 'Pending', color: '#D97706', bg: '#FEF3C7' },
};
// ─── Icons (inline SVGs) ──────────────────────────────────────────────────────
const SearchIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
  </svg>);
const PaperclipIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
  </svg>);
const EmojiIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/>
  </svg>);
const SendIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>);
const MoreIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="19" r="1"/>
  </svg>);
const CheckIcon = ({ color = '#94A3B8' }) => (<svg width="14" height="11" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>);
const DoubleCheckIcon = ({ color = '#94A3B8' }) => (<svg width="20" height="11" viewBox="0 0 32 16" fill="none">
    <polyline points="2 8 7 13 19 1" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="10 8 15 13 27 1" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>);
const ClockIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>);
const AlertIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>);
const BackIcon = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>);
const ChevronDownIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>);
const RefreshIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
  </svg>);
const AVATAR_COLORS = ['#2563EB', '#0891B2', '#059669', '#D97706', '#7C3AED', '#BE185D', '#DC2626'];
const formatMessageTime = (value) => {
    if (!value)
        return '';
    // Append Z to treat as UTC so browser converts to local time (IST)
    const utcValue = value.endsWith('Z') ? value : value + 'Z';
    const date = new Date(utcValue);
    if (Number.isNaN(date.getTime()))
        return '';
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday)
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString())
        return 'Yesterday';
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};
const getInitials = (name = '') => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0)
        return 'WA';
    return parts.slice(0, 2).map(part => part[0]).join('').toUpperCase();
};
const getMessageContent = (message) => {
    if (!message)
        return 'No messages yet.';
    if (message.body)
        return message.body;
    if (message.media_mime_type)
        return `${message.msg_type || 'Media'} attachment`;
    return message.msg_type ? `${message.msg_type} message` : 'Message';
};
const normalizeMessage = (message) => ({
    id: String(message.id || message.wa_message_id || Date.now()),
    type: message.direction === 'outbound' ? 'outbound' : 'inbound',
    content: getMessageContent(message),
    timestamp: formatMessageTime(message.created_at),
    status: message.status || undefined,
});
const normalizeConversation = (conversation, messages = []) => {
    const displayName = conversation.display_name || conversation.phone_number || conversation.wa_id || 'WhatsApp User';
    const latestMessage = messages[messages.length - 1];
    const colorIndex = Number(conversation.id || 0) % AVATAR_COLORS.length;
    return {
        id: String(conversation.id),
        customerName: displayName,
        whatsappNumber: conversation.phone_number || conversation.wa_id || '',
        avatarInitials: getInitials(displayName),
        avatarColor: AVATAR_COLORS[colorIndex],
        lastMessage: getMessageContent(latestMessage),
        timestamp: formatMessageTime(conversation.last_msg_at || latestMessage?.created_at || conversation.created_at),
        unread: 0,
        status: conversation.active === false ? 'closed' : 'open',
        messages: messages.map(normalizeMessage),
    };
};
const InboxIllustration = () => (<svg width="120" height="120" viewBox="0 0 120 120" fill="none">
    <rect x="15" y="25" width="90" height="70" rx="8" fill="#EFF6FF" stroke="#BFDBFE" strokeWidth="1.5"/>
    <rect x="25" y="40" width="70" height="8" rx="4" fill="#BFDBFE"/>
    <rect x="25" y="54" width="50" height="8" rx="4" fill="#DBEAFE"/>
    <rect x="25" y="68" width="60" height="8" rx="4" fill="#DBEAFE"/>
    <circle cx="85" cy="35" r="18" fill="#2563EB"/>
    <path d="M78 35l5 5 9-9" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>);
const SelectIllustration = () => (<svg width="120" height="120" viewBox="0 0 120 120" fill="none">
    <rect x="10" y="20" width="45" height="80" rx="8" fill="#EFF6FF" stroke="#BFDBFE" strokeWidth="1.5"/>
    <rect x="18" y="32" width="29" height="6" rx="3" fill="#BFDBFE"/>
    <rect x="18" y="44" width="21" height="6" rx="3" fill="#DBEAFE"/>
    <rect x="18" y="56" width="25" height="6" rx="3" fill="#DBEAFE"/>
    <rect x="18" y="68" width="18" height="6" rx="3" fill="#DBEAFE"/>
    <rect x="65" y="20" width="45" height="80" rx="8" fill="#F8FAFC" stroke="#E2E8F0" strokeWidth="1.5" strokeDasharray="4 3"/>
    <circle cx="87" cy="60" r="14" fill="#BFDBFE"/>
    <path d="M87 54v6l4 2" stroke="#2563EB" strokeWidth="2" strokeLinecap="round"/>
  </svg>);
// ─── Components ───────────────────────────────────────────────────────────────
function Avatar({ initials, color, size = 40 }) {
    return (<div style={{
            width: size, height: size, borderRadius: '50%',
            background: color + '20', border: `1.5px solid ${color}40`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, fontSize: size * 0.34, fontWeight: 600,
            color: color, letterSpacing: '0.02em',
            userSelect: 'none',
        }}>
      {initials}
    </div>);
}
function StatusBadge({ status }) {
    const cfg = STATUS_CONFIG[status];
    return (<span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '2px 8px', borderRadius: 99,
            fontSize: 11, fontWeight: 500, lineHeight: 1.6,
            color: cfg.color, background: cfg.bg,
        }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.color, display: 'inline-block' }}/>
      {cfg.label}
    </span>);
}
function SkeletonConversationItem() {
    return (<div style={{ padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <div className="skeleton" style={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0 }}/>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div className="skeleton" style={{ width: '60%', height: 13 }}/>
        <div className="skeleton" style={{ width: '40%', height: 11 }}/>
        <div className="skeleton" style={{ width: '80%', height: 11 }}/>
      </div>
    </div>);
}
function SkeletonMessage({ isOutbound = false }) {
    return (<div style={{ display: 'flex', justifyContent: isOutbound ? 'flex-end' : 'flex-start', marginBottom: 12 }}>
      {!isOutbound && <div className="skeleton" style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, marginRight: 10 }}/>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: isOutbound ? 'flex-end' : 'flex-start', maxWidth: '60%' }}>
        <div className="skeleton" style={{ width: isOutbound ? 200 : 240, height: 14 }}/>
        <div className="skeleton" style={{ width: isOutbound ? 140 : 180, height: 14 }}/>
        <div className="skeleton" style={{ width: 60, height: 10 }}/>
      </div>
    </div>);
}
function MessageStatusIcon({ status }) {
    if (!status) return null;
    if (status === 'pending') return <span style={{ color: '#94A3B8', display: 'flex', alignItems: 'center' }}><ClockIcon /></span>;
    if (status === 'failed') return <span style={{ color: '#DC2626', display: 'flex', alignItems: 'center' }}><AlertIcon /></span>;
    if (status === 'sent') return <span style={{ display: 'flex', alignItems: 'center' }}><CheckIcon color="#94A3B8" /></span>;
    if (status === 'delivered') return <span style={{ display: 'flex', alignItems: 'center' }}><DoubleCheckIcon color="#94A3B8" /></span>;
    return <span style={{ display: 'flex', alignItems: 'center' }}><DoubleCheckIcon color="#2563EB" /></span>;
}
function MessageBubble({ message }) {
    const isOut = message.type === 'outbound';
    return (<div className="message-in" style={{
            display: 'flex',
            flexDirection: isOut ? 'row-reverse' : 'row',
            alignItems: 'flex-end',
            gap: 10,
            marginBottom: 12,
        }}>
      {!isOut && (<div style={{
                width: 28, height: 28, borderRadius: '50%',
                background: '#E0E7FF', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 600, color: '#4338CA', flexShrink: 0,
            }}>
          {message.id.slice(-1).toUpperCase()}
        </div>)}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: isOut ? 'flex-end' : 'flex-start', maxWidth: '72%' }}>
        <div style={{
            padding: '10px 14px',
            borderRadius: isOut ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
            background: isOut ? '#2563EB' : '#F1F5F9',
            color: isOut ? '#FFFFFF' : '#0F172A',
            fontSize: 14, lineHeight: 1.6,
            boxShadow: isOut ? '0 1px 4px rgba(37,99,235,0.2)' : '0 1px 3px rgba(0,0,0,0.04)',
            wordBreak: 'break-word',
        }}>
          {message.content}
        </div>
        <div style={{
            display: 'flex', alignItems: 'center', gap: 4,
            marginTop: 4, fontSize: 11, color: '#94A3B8',
        }}>
          <span>{message.timestamp}</span>
          {isOut && <MessageStatusIcon status={message.status}/>}
        </div>
      </div>
    </div>);
}
function ConversationItem({ conv, isSelected, onClick, draft }) {
    const [hovered, setHovered] = useState(false);
    return (<button onClick={onClick} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} aria-label={`Conversation with ${conv.customerName}`} aria-pressed={isSelected} style={{
            width: '100%', border: 'none', cursor: 'pointer', textAlign: 'left',
            padding: '14px 16px',
            background: isSelected ? '#EFF6FF' : hovered ? '#F8FAFC' : conv.unread > 0 ? '#F8FAFF' : 'transparent',
            borderLeft: isSelected ? '3px solid #2563EB' : '3px solid transparent',
            display: 'flex', gap: 12, alignItems: 'flex-start',
            transition: 'background 150ms ease, border-color 150ms ease',
            position: 'relative',
        }}>
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <Avatar initials={conv.avatarInitials} color={conv.avatarColor} size={40}/>
        {conv.unread > 0 && (<span style={{
                position: 'absolute', top: -2, right: -2,
                width: 10, height: 10, borderRadius: '50%',
                background: '#2563EB', border: '2px solid white',
            }}/>)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
          <span style={{
            fontSize: 14, fontWeight: conv.unread > 0 ? 600 : 500,
            color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            maxWidth: 160,
        }}>
            {conv.customerName}
          </span>
          <span style={{
            fontSize: 11,
            fontWeight: conv.unread > 0 ? 700 : 500,
            color: conv.unread > 0 ? '#2563EB' : '#94A3B8',
            flexShrink: 0,
            marginLeft: 8,
        }}>
            {conv.timestamp}
          </span>
        </div>
        {/* <div style={{ fontSize: 12, color: '#64748B', marginBottom: 6 }}>{conv.whatsappNumber}</div> */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          {draft && draft.trim() ? (
            <span style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
              <span style={{ color: '#DC2626', fontWeight: 600 }}>Draft: </span>
              <span style={{ color: '#94A3B8' }}>{draft.trim()}</span>
            </span>
          ) : (
            <span style={{
              fontSize: 13, color: conv.unread > 0 ? '#0F172A' : '#64748B',
              fontWeight: conv.unread > 0 ? 500 : 400,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
            }}>
              {conv.lastMessage}
            </span>
          )}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
            {conv.unread > 0 && (<span style={{
                minWidth: 18, height: 18, borderRadius: 99,
                background: '#2563EB', color: 'white',
                fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '0 5px',
            }}>
                {conv.unread}
              </span>)}
          </div>
        </div>
      </div>
    </button>);
}
const MAX_CHARS = 4096;
function ReplyComposer({ onSend, disabled = false, noConversation = false, value, onChange }) {
    const [composerState, setComposerState] = useState('idle');
    const [validationMsg, setValidationMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [focused, setFocused] = useState(false);
    const [shake, setShake] = useState(false);
    const textareaRef = useRef(null);
    const wrapperRef = useRef(null);
    const isDisabled = disabled || noConversation || composerState === 'sending';
    const charCount = value.length;
    const overLimit = charCount > MAX_CHARS;
    const canSend = value.trim().length > 0 && !isDisabled && !overLimit;
    // Auto-grow textarea
    const autoGrow = () => {
        const el = textareaRef.current;
        if (!el)
            return;
        el.style.height = 'auto';
        const lineH = 22;
        const minH = lineH * 2;
        const maxH = lineH * 6;
        el.style.height = Math.min(Math.max(el.scrollHeight, minH), maxH) + 'px';
    };
    useEffect(() => { autoGrow(); }, [value]);
    const triggerShake = () => {
        setShake(true);
        setTimeout(() => setShake(false), 400);
    };
    const handleSend = async () => {
        var _a, _b;
        const trimmed = value.trim();
        if (noConversation || isDisabled)
            return;
        if (!trimmed) {
            setValidationMsg('Message cannot be empty.');
            triggerShake();
            (_a = textareaRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            return;
        }
        if (overLimit) {
            setValidationMsg(`Message exceeds ${MAX_CHARS} character limit.`);
            return;
        }
        setValidationMsg('');
        setErrorMsg('');
        setComposerState('sending');
        try {
            await onSend(trimmed);
            onChange('');
            setComposerState('idle');
            (_b = textareaRef.current) === null || _b === void 0 ? void 0 : _b.focus();
        }
        catch (error) {
            setComposerState('error');
            setErrorMsg(error.message || 'Unable to send your message. Please try again.');
        }
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };
    const handleRetry = () => {
        setErrorMsg('');
        setComposerState('idle');
        handleSend();
    };
    const placeholder = noConversation
        ? 'Select a conversation to start replying.'
        : 'Type your reply...';
    const borderColor = composerState === 'error'
        ? '#DC2626'
        : focused
            ? '#2563EB'
            : '#E2E8F0';
    return (<div style={{ borderTop: '1px solid #E2E8F0', padding: '12px 16px 16px', background: 'white', flexShrink: 0 }}>

      {/* Error banner */}
      {errorMsg && (<div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10,
                padding: '9px 12px', marginBottom: 10,
                animation: 'fadeIn 200ms ease-out',
            }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#DC2626', flexShrink: 0 }}><AlertIcon /></span>
            <span style={{ fontSize: 13, color: '#DC2626', fontWeight: 500 }}>{errorMsg}</span>
          </div>
          <button onClick={handleRetry} style={{
                background: '#DC2626', color: 'white', border: 'none', borderRadius: 8,
                padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                fontFamily: 'inherit', flexShrink: 0, marginLeft: 12,
            }}>
            Retry
          </button>
        </div>)}

      {/* Composer box */}
      <div ref={wrapperRef} style={{
            border: `1.5px solid ${borderColor}`,
            borderRadius: 12, overflow: 'hidden',
            background: isDisabled ? '#F8FAFC' : 'white',
            transition: 'border-color 200ms, box-shadow 200ms',
            boxShadow: focused && !composerState ? '0 0 0 3px #DBEAFE' : 'none',
          animation: shake ? 'doclift-chat-shake 0.35s ease' : 'none',
        }}>
        <textarea ref={textareaRef} value={value} onChange={e => { onChange(e.target.value); setValidationMsg(''); setErrorMsg(''); }} onKeyDown={handleKeyDown} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} disabled={isDisabled} placeholder={placeholder} rows={2} aria-label="Reply message" style={{
            width: '100%', border: 'none', outline: 'none', resize: 'none',
            padding: '12px 14px 6px',
            fontSize: 14, color: '#0F172A', lineHeight: '22px',
            fontFamily: 'inherit', background: 'transparent',
            opacity: noConversation ? 0.45 : 1,
            overflow: 'hidden',
            boxSizing: 'border-box',
        }}/>

        {/* Bottom toolbar */}
        <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '6px 10px 8px',
            borderTop: `1px solid ${focused ? '#EFF6FF' : '#F1F5F9'}`,
            transition: 'border-color 200ms',
        }}>
          <div style={{ display: 'flex', gap: 2 }}>
            <IconBtn label="Attach file" disabled={isDisabled}><PaperclipIcon /></IconBtn>
            <IconBtn label="Insert emoji" disabled={isDisabled}><EmojiIcon /></IconBtn>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* Char counter — only show when approaching limit */}
            {charCount > MAX_CHARS * 0.8 && (<span style={{
                fontSize: 11, fontWeight: 500, transition: 'color 200ms',
                color: overLimit ? '#DC2626' : charCount > MAX_CHARS * 0.95 ? '#D97706' : '#94A3B8',
            }}>
                {charCount}/{MAX_CHARS}
              </span>)}
            <span style={{ fontSize: 11, color: '#CBD5E1' }}>↵ Send · ⇧↵ Newline</span>

            {/* Send button */}
            <button onClick={handleSend} disabled={!canSend} aria-label="Send message" style={{
            height: 34, padding: '0 14px', borderRadius: 9, border: 'none',
            cursor: canSend ? 'pointer' : 'not-allowed',
            background: canSend ? '#2563EB' : '#E2E8F0',
            color: canSend ? 'white' : '#94A3B8',
            display: 'flex', alignItems: 'center', gap: 6,
            fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
            transition: 'all 200ms, transform 80ms',
            transform: 'scale(1)',
        }} onMouseDown={e => { if (canSend)
        (e.currentTarget.style.transform = 'scale(0.95)'); }} onMouseUp={e => { (e.currentTarget.style.transform = 'scale(1)'); }} onMouseLeave={e => { (e.currentTarget.style.transform = 'scale(1)'); }}>
              {composerState === 'sending' ? (<span style={{
                width: 13, height: 13, border: '2px solid white', borderTopColor: 'transparent',
                borderRadius: '50%', display: 'inline-block',
                  animation: 'doclift-chat-spin 0.6s linear infinite',
            }}/>) : <SendIcon />}
              {composerState === 'sending' ? 'Sending…' : 'Send'}
            </button>
          </div>
        </div>
      </div>

      {/* Inline validation */}
      {validationMsg && (<p style={{
                margin: '6px 2px 0', fontSize: 12, color: '#DC2626', fontWeight: 500,
                animation: 'fadeIn 150ms ease-out',
            }}>
          {validationMsg}
        </p>)}
    </div>);
}
function IconBtn({ children, label, disabled = false }) {
    const [hovered, setHovered] = useState(false);
    return (<button aria-label={label} disabled={disabled} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} style={{
            width: 36, height: 36, borderRadius: 8, border: 'none',
            background: hovered && !disabled ? '#F1F5F9' : 'transparent',
            cursor: disabled ? 'not-allowed' : 'pointer',
            color: disabled ? '#CBD5E1' : '#64748B',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 150ms',
        }}>
      {children}
    </button>);
}
function ScrollToBottomBtn({ onClick }) {
    return (<button onClick={onClick} aria-label="Scroll to bottom" style={{
            position: 'absolute', bottom: 16, right: 20,
            width: 36, height: 36, borderRadius: '50%',
            background: 'white', border: '1px solid #E2E8F0',
            boxShadow: '0 4px 12px rgba(0,0,0,0.10)',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#64748B', transition: 'box-shadow 200ms',
        }}>
      <ChevronDownIcon />
    </button>);
}
// ─── Main App ─────────────────────────────────────────────────────────────────
export default function Chat() {
    const [conversations, setConversations] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [drafts, setDrafts] = useState({});
    const [composerValue, setComposerValue] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [mobileView, setMobileView] = useState('list');
    const [showScrollBtn, setShowScrollBtn] = useState(false);
    const [showMoreMenu, setShowMoreMenu] = useState(false);
    const messagesEndRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const selectedIdRef = useRef(null);
    const selectedConv = conversations.find(c => c.id === selectedId) || null;
    const selectedMessageCount = selectedConv ? selectedConv.messages.length : 0;
    useEffect(() => {
        selectedIdRef.current = selectedId;
    }, [selectedId]);
    const loadConversations = useCallback(async (showLoader = false) => {
        if (showLoader)
            setLoading(true);
        try {
            const conversationRows = await fetchWhatsappConversations();
            const hydratedConversations = await Promise.all(conversationRows.map(async (conversation) => {
                const messages = await fetchWhatsappMessages(conversation.id);
                return normalizeConversation(conversation, messages);
            }));
            setConversations(prev => hydratedConversations.map(conversation => {
                const previousConversation = prev.find(item => item.id === conversation.id);
                // On initial load or new conversation — no unread
                if (!previousConversation || showLoader) {
                    return { ...conversation, unread: 0 };
                }
                const previousMessageIds = new Set(previousConversation.messages.map(message => message.id));
                const newInboundCount = conversation.messages.filter(message => message.type === 'inbound' && !previousMessageIds.has(message.id)).length;
                return {
                    ...conversation,
                    unread: selectedIdRef.current === conversation.id
                        ? 0
                        : previousConversation.unread + newInboundCount,
                };
            }));
            if (hydratedConversations.length > 0 && !selectedIdRef.current) {
                setSelectedId(hydratedConversations[0].id);
            }
            setErrorMessage('');
        }
        catch (error) {
            setErrorMessage(error.message || 'Unable to load WhatsApp conversations.');
            if (showLoader)
                setConversations([]);
        }
        finally {
            if (showLoader)
                setLoading(false);
        }
    }, []);
    const loadConversationMessages = useCallback(async (conversationId) => {
        setMessagesLoading(true);
        try {
            const messages = await fetchWhatsappMessages(conversationId);
            const normalizedMessages = messages.map(normalizeMessage);
            const latestMessage = messages[messages.length - 1];
            setConversations(prev => prev.map(conversation => conversation.id === conversationId
                ? {
                    ...conversation,
                    lastMessage: getMessageContent(latestMessage),
                    timestamp: formatMessageTime(latestMessage?.created_at),
                    messages: normalizedMessages,
                }
                : conversation));
            setErrorMessage('');
        }
        catch (error) {
            setErrorMessage(error.message || 'Unable to load this WhatsApp conversation.');
        }
        finally {
            setMessagesLoading(false);
        }
    }, []);
    const [toastPopup, setToastPopup] = useState(null);

    useEffect(() => {
        if (window.Notification && Notification.permission !== "granted" && Notification.permission !== "denied") {
            Notification.requestPermission();
        }
    }, []);

    useEffect(() => {
        loadConversations(true);

        const activeTenantId = localStorage.getItem('tenant_id') || '67a40a70-ef72-48cb-ba69-28e5ca58a78d';
        const streamUrl = getWhatsAppStreamUrl(activeTenantId);
        const eventSource = new EventSource(streamUrl);

        eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                // console.log('⚡ [Realtime SSE Push Event]:', data);
                if (data.event === 'inbound_message' || data.event === 'conversation_updated') {
                    const incomingConvId = data.data?.conversation_id ? String(data.data.conversation_id) : null;
                    loadConversations(false);
                    if (incomingConvId && (!selectedIdRef.current || selectedIdRef.current === incomingConvId)) {
                        setSelectedId(incomingConvId);
                        loadConversationMessages(incomingConvId);
                    } else if (selectedIdRef.current) {
                        loadConversationMessages(selectedIdRef.current);
                    }
                    const msgBody = data.data?.body || data.data?.text || 'New message received in WhatsApp Inbox';
                    const sender = data.data?.sender_name || data.data?.customer_name || 'Customer';
                    setToastPopup({
                        title: `💬 New WhatsApp Message from ${sender}`,
                        body: msgBody,
                        convId: incomingConvId,
                    });
                    setTimeout(() => setToastPopup(null), 8000);

                    if (window.Notification && Notification.permission === "granted") {
                        new Notification("💬 New WhatsApp Message", {
                            body: msgBody,
                            icon: "/logo192.png"
                        });
                    }
                }
            } catch (e) {
                // console.error("Error parsing real-time SSE event:", e);
            }
        };

        return () => {
            eventSource.close();
        };
    }, [loadConversations, loadConversationMessages]);
    // Auto-scroll to bottom on new messages
    useEffect(() => {
        if (selectedConv && messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [selectedConv, selectedMessageCount]);
    const handleScroll = useCallback(() => {
        const el = scrollContainerRef.current;
        if (!el)
            return;
        const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        setShowScrollBtn(!atBottom);
    }, []);
    const scrollToBottom = () => {
        var _a;
        (_a = messagesEndRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: 'smooth' });
    };
    const handleSend = async (text) => {
        if (!selectedId)
            return;
        const optimisticId = `pending-${Date.now()}`;
        const newMsg = {
            id: optimisticId,
            type: 'outbound',
            content: text,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            status: 'pending',
        };
        setConversations(prev => prev.map(c => c.id === selectedId
            ? { ...c, messages: [...c.messages, newMsg], lastMessage: text, timestamp: 'just now', unread: 0 }
            : c));
        try {
            await sendWhatsappReply(selectedId, text);
            await loadConversationMessages(selectedId);
        }
        catch (error) {
            setConversations(prev => prev.map(c => c.id === selectedId
                ? {
                    ...c,
                    messages: c.messages.map(message => message.id === optimisticId
                        ? { ...message, status: 'failed' }
                        : message),
                }
                : c));
            throw error;
        }
    };
    const handleSelectConv = (id) => {
        if (selectedId && selectedId !== id) {
            setDrafts(prev => ({ ...prev, [selectedId]: composerValue }));
        }
        setComposerValue(drafts[id] || '');
        setSelectedId(id);
        setMobileView('thread');
        setConversations(prev => prev.map(c => c.id === id ? { ...c, unread: 0 } : c));
        setShowMoreMenu(false);
        loadConversationMessages(id);
    };
    const filteredConvs = conversations.filter(c => {
        const matchesSearch = c.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            c.whatsappNumber.includes(searchQuery) ||
            c.lastMessage.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesSearch;
    });
    const totalUnread = conversations.reduce((sum, c) => sum + c.unread, 0);
    return (<div className="doclift-chat-page" style={{ fontFamily: 'Inter, sans-serif', minHeight: 'calc(100vh - 72px)', height: 'calc(100vh - 72px)', display: 'flex', flexDirection: 'column', background: '#F8FAFC', overflow: 'hidden' }}>
      {/* ── Top Navigation ── */}
      <header style={{
            height: 56, background: 'white', borderBottom: '1px solid #E2E8F0',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '0 20px', flexShrink: 0, zIndex: 40,
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 30, height: 30, borderRadius: 8,
            background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="white" opacity="0.8"/>
              <polyline points="14 2 14 8 20 8" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
              <line x1="8" y1="13" x2="16" y2="13" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
              <line x1="8" y1="17" x2="14" y2="17" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#0F172A', letterSpacing: '-0.01em' }}>DocLift</span>
        </div>

        <nav style={{ display: 'flex', gap: 2 }}>
          <button style={{
            padding: '6px 14px', borderRadius: 8, border: 'none',
            background: '#EFF6FF', color: '#2563EB',
            fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', gap: 6,
        }}>
            Support Inbox
            {totalUnread > 0 && (<span style={{
                background: '#2563EB', color: 'white',
                borderRadius: 99, fontSize: 10, fontWeight: 600,
                padding: '1px 5px', display: 'inline-block',
            }}>
                {totalUnread}
              </span>)}
          </button>
        </nav>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%',
            background: '#2563EB', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 600, color: 'white',
        }}>
            AS
          </div>
        </div>
      </header>

      {/* ── Body ── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>

        {/* ── Left Panel ── */}
        <aside style={{
            width: 360, flexShrink: 0,
            background: 'white', borderRight: '1px solid #E2E8F0',
            display: 'flex', flexDirection: 'column',
            // Mobile: show only when mobileView === 'list'
            ...(window.innerWidth < 768 ? { display: mobileView === 'list' ? 'flex' : 'none', position: 'absolute', inset: 0, zIndex: 30, width: '100%' } : {}),
        }}>
          {/* Panel header */}
          <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid #F1F5F9' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h1 style={{ fontSize: 18, fontWeight: 700, color: '#0F172A', margin: 0, letterSpacing: '-0.02em' }}>
                Support Inbox
              </h1>
              <span style={{
            fontSize: 12, fontWeight: 600, color: '#64748B',
            background: '#F1F5F9', padding: '3px 8px', borderRadius: 99,
        }}>
                {filteredConvs.length} conversations
              </span>
            </div>

            {/* Search */}
            <div style={{ position: 'relative', marginBottom: 10 }}>
              <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' }}>
                <SearchIcon />
              </span>
              <input type="text" placeholder="Search conversations…" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} style={{
            width: '100%', padding: '9px 12px 9px 38px',
            border: '1.5px solid #E2E8F0', borderRadius: 10,
            fontSize: 13, color: '#0F172A', outline: 'none',
            fontFamily: 'inherit', background: '#F8FAFC',
            transition: 'border-color 150ms',
            boxSizing: 'border-box',
        }} onFocus={e => { e.target.style.borderColor = '#2563EB'; e.target.style.background = 'white'; }} onBlur={e => { e.target.style.borderColor = '#E2E8F0'; e.target.style.background = '#F8FAFC'; }}/>
            </div>

            {errorMessage && (<div style={{
                background: '#FEF2F2',
                border: '1px solid #FECACA',
                borderRadius: 10,
                color: '#DC2626',
                fontSize: 12,
                fontWeight: 500,
                lineHeight: 1.5,
                padding: '8px 10px',
            }}>
              {errorMessage}
            </div>)}

          </div>

          {/* Conversation list */}
          <div className="scroll-container" style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (Array.from({ length: 6 }).map((_, i) => <SkeletonConversationItem key={i}/>)) : filteredConvs.length === 0 ? (<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 32, textAlign: 'center' }}>
                <InboxIllustration />
                <h3 style={{ fontSize: 16, fontWeight: 600, color: '#0F172A', margin: '16px 0 8px' }}>
                  No customer conversations yet.
                </h3>
                <p style={{ fontSize: 13, color: '#64748B', lineHeight: 1.6, margin: '0 0 16px' }}>
                  New WhatsApp conversations will appear here.
                </p>
                <button onClick={() => {
                setSearchQuery('');
                loadConversations(true);
            }} style={{
                padding: '8px 16px', borderRadius: 10, border: '1.5px solid #E2E8F0',
                background: 'white', color: '#0F172A', fontSize: 13, fontWeight: 500,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'inherit',
            }}>
                  <RefreshIcon /> Refresh
                </button>
              </div>) : (filteredConvs.map(conv => (<ConversationItem key={conv.id} conv={conv} isSelected={selectedId === conv.id} onClick={() => handleSelectConv(conv.id)} draft={selectedId === conv.id ? '' : drafts[conv.id] || ''}/>)))}
          </div>
        </aside>

        {/* ── Right Panel ── */}
        <main style={{
            flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden',
            background: '#F8FAFC',
            ...(window.innerWidth < 768 ? { display: mobileView === 'thread' ? 'flex' : 'none', position: 'absolute', inset: 0, zIndex: 30 } : {}),
        }}>
          {!selectedConv ? (
        /* No conversation selected */
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, textAlign: 'center' }}>
                <SelectIllustration />
                <h2 style={{ fontSize: 20, fontWeight: 600, color: '#0F172A', margin: '20px 0 10px', letterSpacing: '-0.02em' }}>
                  Select a conversation
                </h2>
                <p style={{ fontSize: 14, color: '#64748B', lineHeight: 1.7, maxWidth: 280, margin: 0 }}>
                  Choose a conversation from the left to start responding to your customers.
                </p>
              </div>
              <ReplyComposer onSend={() => {}} noConversation value="" onChange={() => {}}/>
            </div>) : (<>
              {/* Conversation header */}
              <div style={{
                padding: '14px 20px', background: 'white',
                borderBottom: '1px solid #E2E8F0',
                display: 'flex', alignItems: 'center', gap: 12,
                flexShrink: 0,
            }}>
                {/* Mobile back */}
                <button onClick={() => setMobileView('list')} aria-label="Back to conversations" style={{
                width: 36, height: 36, borderRadius: 8, border: 'none',
                background: '#F1F5F9', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#64748B', flexShrink: 0,
                // Only show on mobile
            }}>
                  <BackIcon />
                </button>

                <Avatar initials={selectedConv.avatarInitials} color={selectedConv.avatarColor} size={44}/>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 2 }}>
                    <h2 style={{ fontSize: 16, fontWeight: 600, color: '#0F172A', margin: 0 }}>{selectedConv.customerName}</h2>
                    <StatusBadge status={selectedConv.status}/>
                  </div>
                  <p style={{ fontSize: 12, color: '#64748B', margin: 0 }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="#25D366" style={{ marginRight: 4, verticalAlign: 'middle' }}>
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347"/>
                    </svg>
                    {selectedConv.whatsappNumber}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <div style={{ position: 'relative' }}>
                    <button onClick={() => setShowMoreMenu(v => !v)} aria-label="More options" style={{
                width: 36, height: 36, borderRadius: 8, border: 'none',
                background: showMoreMenu ? '#F1F5F9' : 'transparent',
                cursor: 'pointer', color: '#64748B',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                      <MoreIcon />
                    </button>
                    {showMoreMenu && (<div style={{
                    position: 'absolute', right: 0, top: 44, zIndex: 50,
                    background: 'white', border: '1px solid #E2E8F0',
                    borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
                    minWidth: 180, overflow: 'hidden',
                }}>
                        {['Mark as resolved', 'Close conversation', 'Assign to agent', 'View customer profile'].map(action => (<button key={action} style={{
                        display: 'block', width: '100%', padding: '10px 14px',
                        border: 'none', background: 'transparent', cursor: 'pointer',
                        textAlign: 'left', fontSize: 13, color: '#0F172A', fontFamily: 'inherit',
                    }} onMouseEnter={e => { e.target.style.background = '#F8FAFC'; }} onMouseLeave={e => { e.target.style.background = 'transparent'; }}>
                            {action}
                          </button>))}
                      </div>)}
                  </div>
                </div>
              </div>

              {/* Message thread */}
              <div ref={scrollContainerRef} onScroll={handleScroll} className="scroll-container" style={{
                flex: 1, overflowY: 'auto',
                padding: '20px 24px',
                position: 'relative',
            }}>
                {loading || messagesLoading ? (<>
                    <SkeletonMessage />
                    <SkeletonMessage isOutbound/>
                    <SkeletonMessage />
                    <SkeletonMessage isOutbound/>
                  </>) : (selectedConv.messages.map(msg => (<MessageBubble key={msg.id} message={msg}/>)))}
                <div ref={messagesEndRef}/>
              </div>

              {/* Scroll to bottom */}
              {showScrollBtn && (<div style={{ position: 'relative', height: 0 }}>
                  <ScrollToBottomBtn onClick={scrollToBottom}/>
                </div>)}

              {/* Reply composer */}
              <ReplyComposer
                onSend={async (text) => {
                    await handleSend(text);
                    setDrafts(prev => { const d = { ...prev }; delete d[selectedId]; return d; });
                }}
                disabled={selectedConv.status === 'closed'}
                value={composerValue}
                onChange={(val) => {
                    setComposerValue(val);
                    setDrafts(prev => ({ ...prev, [selectedId]: val }));
                }}
              />
            </>)}
        </main>
      </div>
      {toastPopup && (
        <div
            onClick={() => {
                if (toastPopup.convId) {
                    setSelectedId(toastPopup.convId);
                    loadConversationMessages(toastPopup.convId);
                }
                setToastPopup(null);
            }}
            style={{
                position: 'fixed',
                top: '24px',
                right: '24px',
                zIndex: 9999,
                background: '#0F172A',
                color: '#FFFFFF',
                padding: '16px 20px',
                borderRadius: '12px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
                border: '1px solid #3B82F6',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                minWidth: '300px',
                cursor: 'pointer',
            }}
        >
            <div style={{ fontSize: '24px' }}>💬</div>
            <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#60A5FA' }}>{toastPopup.title}</div>
                <div style={{ fontSize: '13px', color: '#E2E8F0', marginTop: '2px' }}>{toastPopup.body}</div>
                <div style={{ fontSize: '11px', color: '#93C5FD', marginTop: '4px' }}>Click to open conversation →</div>
            </div>
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    setToastPopup(null);
                }}
                style={{ background: 'none', border: 'none', color: '#94A3B8', cursor: 'pointer', fontSize: '18px', padding: '0 4px' }}
            >×</button>
        </div>
      )}
    </div>);
}
